import { ListItemNode, SerializedListItemNode } from '@lexical/list';
import {
  $applyNodeReplacement,
  EditorConfig,
  LexicalNode,
  LexicalUpdateJSON,
  NodeKey,
} from 'lexical';
import { toPx } from 'utils/toPx';
import { setNodeLineHeightToDOM } from '../Plugins/TextBlockPlugin/utils';
import { $isCustomListNode } from './CustomListNode';
const INDENT_STEP = 1.5;

export type SerializedCustomListItemNode = SerializedListItemNode & { lineHeight: number | undefined; fontSize: number | undefined };

/**
* Custom List item node with dynamic line-height functionality
*/
export class CustomListItemNode extends ListItemNode {
  __lineHeight: number | undefined;

  __fontSize: number | undefined;

  constructor(value?: number, checked?: boolean, key?: NodeKey, fontSize?: number, lineHeight?: number) {
    super(value, checked, key);
    this.__lineHeight = lineHeight;
    this.__fontSize = fontSize;
  }

  static getType(): string {
    return 'custom-list-item';
  }

  static clone(node: CustomListItemNode): CustomListItemNode {
    return new CustomListItemNode(node.__value, node.__checked, node.__key, node.__fontSize, node.__lineHeight);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.style.fontSize = toPx(this.__fontSize) ?? '';
    setNodeLineHeightToDOM(element, this.__lineHeight, this.__fontSize);
    const hasNestedList = $isCustomListNode(this.getFirstChild());
    if (!hasNestedList) {
      const dir = this.getDirection() ?? 'ltr';
      const marginProperty = { ltr: 'marginLeft', rtl: 'marginRight' }[dir];
      const currentIndentLevel = this.getIndent() + 1;
      element.style[marginProperty] = `${currentIndentLevel * INDENT_STEP}em`;
    }

    return element;
  }

  updateDOM(prevNode: CustomListItemNode, element: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode, element, config);

    const prevLineHeight = prevNode.__lineHeight;
    const nextLineHeight = this.__lineHeight;
    const prevSize = prevNode.__fontSize;
    const nextSize = this.__fontSize;

    if ((prevLineHeight !== nextLineHeight) || (prevSize !== nextSize)) {
      element.style.fontSize = toPx(nextSize) ?? '';
      setNodeLineHeightToDOM(element, this.__lineHeight, this.__fontSize);
    }

    return isUpdated;
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedCustomListItemNode>,
  ): this {
    return super.updateFromJSON(serializedNode)
      .setFontSize(serializedNode.fontSize)
      .setLineHeight(serializedNode.lineHeight);
  }

  static importJSON(
    serializedNode: SerializedCustomListItemNode,
  ): CustomListItemNode {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    return $createCustomListItemNode().updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedCustomListItemNode {
    const json = super.exportJSON();

    return {
      ...json,
      lineHeight: this.__lineHeight,
      fontSize: this.__fontSize,
    };
  }

  setLineHeight(lineHeight: number | undefined): this {
    this.getWritable().__lineHeight = lineHeight;

    return this;
  }

  getLineHeight(): number | undefined {
    return this.getLatest().__lineHeight;
  }

  setFontSize(fontSize: number | undefined): this {
    this.getWritable().__fontSize = fontSize;

    return this;
  }

  getFontSize(): number | undefined {
    return this.getLatest().__fontSize;
  }
}

export function $isCustomListItemNode(
  node: LexicalNode | null | undefined,
): node is CustomListItemNode {
  return node instanceof CustomListItemNode;
}

export function $createCustomListItemNode(fontSize?: number, lineHeight?: number, checked?: boolean): CustomListItemNode {
  return $applyNodeReplacement(new CustomListItemNode(undefined, checked, undefined, fontSize, lineHeight));
}
